import { useQuery } from "@tanstack/react-query";
import { equipmentReportPdfApi } from "./axios";
import {
  ServiceResponseI,
  EquipmentReportPdfI,
  EquipmentReportPdfPaginationI,
  CreateEquipmentReportResponseI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

export const getAllEquipmentReportPdf = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: boolean;
  equipmentId?: number;
  sorting?: string;
}): Promise<ServiceResponseI<EquipmentReportPdfPaginationI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.search) searchParams.append("search", params.search);
    if (params?.status !== undefined)
      searchParams.append("status", params.status.toString());
    if (params?.equipmentId)
      searchParams.append("equipmentId", params.equipmentId.toString());
    if (params?.sorting) searchParams.append("sorting", params.sorting);

    const { data } = await equipmentReportPdfApi.get<
      ServiceResponseI<EquipmentReportPdfPaginationI>
    >(`/all`, {
      params: searchParams,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    if (obj?.status === 400) {
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getAllEquipmentReportsByEquipmentId = async (
  equipmentId: number
): Promise<ServiceResponseI<EquipmentReportPdfI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await equipmentReportPdfApi.get<
      ServiceResponseI<EquipmentReportPdfI[]>
    >(`/equipment/${equipmentId}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    if (obj?.status === 400) {
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getEquipmentReportPdfById = async (
  id: number
): Promise<ServiceResponseI<EquipmentReportPdfI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await equipmentReportPdfApi.get<
      ServiceResponseI<EquipmentReportPdfI>
    >(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    if (obj?.status === 400) {
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const validateEquipmentReportExists = async (
  equipmentId: number,
  src: string
): Promise<ServiceResponseI<{ exists: boolean }>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("equipmentId", equipmentId.toString());
    params.append("src", src);

    const { data } = await equipmentReportPdfApi.get<
      ServiceResponseI<{ exists: boolean }>
    >(`/validate-exists`, {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    if (obj?.status === 400) {
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getEquipmentReportBySrc = async (
  src: string
): Promise<ServiceResponseI<EquipmentReportPdfI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("src", src);

    const { data } = await equipmentReportPdfApi.get<
      ServiceResponseI<EquipmentReportPdfI>
    >(`/by-src`, {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    if (obj?.status === 400) {
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const equipmentReportPdfPost = async (
  file: File,
  equipmentId: number,
  reportDate?: string
): Promise<ServiceResponseI<CreateEquipmentReportResponseI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("equipmentId", equipmentId.toString());
    if (reportDate) {
      formData.append("reportDate", reportDate);
    }

    const { data } = await equipmentReportPdfApi.post<
      ServiceResponseI<CreateEquipmentReportResponseI>
    >("", formData, {
      headers: {
        Authorization: validateToken,
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const equipmentReportPdfPut = async (
  id: number,
  updateData: {
    src?: string;
    status?: boolean;
  }
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await equipmentReportPdfApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateData,
      {
        headers: { Authorization: validateToken },
      }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const equipmentReportPdfDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await equipmentReportPdfApi.delete<
      ServiceResponseI<string>
    >(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const equipmentReportPdfHardDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await equipmentReportPdfApi.delete<
      ServiceResponseI<string>
    >(`/hard/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const downloadEquipmentReportPdf = async (id: number): Promise<void> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');

        const response = await equipmentReportPdfApi.get(`/download/${id}`, {
            responseType: 'blob', // Importante: especificar que esperamos un blob
            headers: { 
                'Authorization': validateToken,
                'Accept': 'application/pdf'
            }
        });

        // Crear un blob desde la respuesta
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // Crear URL temporal para el blob
        const url = window.URL.createObjectURL(blob);
        
        // Crear elemento anchor temporal para la descarga
        const link = document.createElement('a');
        link.href = url;
        
        // Obtener el nombre del archivo desde los headers o usar uno por defecto
        const contentDisposition = response.headers['content-disposition'];
        let filename = `reporte-${id}.pdf`;
        
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        link.download = filename;
        
        // Simular click para iniciar descarga
        document.body.appendChild(link);
        link.click();
        
        // Limpiar
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
    } catch (err) {
        const obj = (err as AxiosError).response;
        if (obj?.status === 401) {
            LogoutSystem();
        }
        throw new Error((obj?.data as Error)?.message || 'Error al descargar el archivo');
    }
};

// Nueva función para ver el PDF en nueva pestaña (sin descargar)
export const viewEquipmentReportPdf = async (id: number): Promise<void> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');

        const response = await equipmentReportPdfApi.get(`/download/${id}`, {
            responseType: 'blob',
            headers: { 
                'Authorization': validateToken,
                'Accept': 'application/pdf'
            }
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Abrir en nueva pestaña
        window.open(url, '_blank');
        
        // Opcional: limpiar la URL después de un tiempo
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 1000);
        
    } catch (err) {
        const obj = (err as AxiosError).response;
        if (obj?.status === 401) {
            LogoutSystem();
        }
        throw new Error((obj?.data as Error)?.message || 'Error al abrir el archivo');
    }
};

// Función alternativa para descargar directamente por URL relativa (como fallback)
export const downloadEquipmentReportPdfByUrl = async (src: string, filename?: string): Promise<void> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        
        // Construir la URL completa del servidor
        const baseURL = equipmentReportPdfApi.defaults.baseURL || '';
        const fullUrl = `${baseURL?.replace('/equipment-report-pdf', '')}${src}`;
        
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/pdf',
                'Authorization': validateToken,
            },
        });

        if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        // Convertir a blob
        const blob = await response.blob();
        
        // Crear URL temporal para el blob
        const url = window.URL.createObjectURL(blob);
        
        // Crear elemento anchor temporal para la descarga
        const link = document.createElement('a');
        link.href = url;
        
        // Usar el filename proporcionado o extraer del src
        const defaultFilename = src.split('/').pop() || 'reporte.pdf';
        link.download = filename || defaultFilename;
        
        // Simular click para iniciar descarga
        document.body.appendChild(link);
        link.click();
        
        // Limpiar
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
    } catch (error: any) {
        console.error('Error downloading PDF by URL:', error);
        throw new Error(error.message || 'Error al descargar el archivo');
    }
};

// Hook personalizado usando React Query para obtener reportes por equipo
export const useEquipmentReports = (
  equipmentId: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["equipment-reports", equipmentId],
    queryFn: () => getAllEquipmentReportsByEquipmentId(equipmentId),
    enabled: enabled && !!equipmentId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
  });
};

// Hook para obtener un reporte específico
export const useEquipmentReport = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["equipment-report", id],
    queryFn: () => getEquipmentReportPdfById(id),
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
  });
};

// Hook para obtener todos los reportes con paginación
export const useAllEquipmentReports = (
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: boolean;
    equipmentId?: number;
    sorting?: string;
  },
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["all-equipment-reports", params],
    queryFn: () => getAllEquipmentReportPdf(params),
    enabled: enabled,
    staleTime: 2 * 60 * 1000, // 2 minutos
    refetchOnWindowFocus: false,
  });
};
